import {
    WarningEmptyFilesCanNotBeAttached,
    WarningInlineImageTypeNotSupported,
} from './filterFilesToAttach.locstring.json';
import loc, { format } from 'owa-localize';
import { AttachmentInfoBarMessageId } from './AttachmentInfoBarMessageId';
import clearAttachmentInfoBarMessages from './clearAttachmentInfoBarMessages';
import {
    AttachmentFile,
    AttachmentFileList,
    AttachmentFileType,
    LocalComputerFile,
} from 'owa-attachment-file-types';
import { isImageFile } from 'owa-file';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';

interface InfoBarMessage {
    messageId: AttachmentInfoBarMessageId;
    message: string;
}

type CreateMessageFunc = (fileNames: string) => InfoBarMessage;

function filterAndShowErrorMessage(
    files: AttachmentFile[],
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    filterPredicate: (file: AttachmentFile) => boolean,
    createMessageFunc: CreateMessageFunc
): AttachmentFile[] {
    const filesFulfillingPredicate = [];
    const filesNotFulfillingPredicate = [];

    files.forEach(file => {
        if (filterPredicate(file)) {
            filesFulfillingPredicate.push(file);
        } else {
            filesNotFulfillingPredicate.push(file);
        }
    });

    if (filesNotFulfillingPredicate.length > 0) {
        const fileNames = filesNotFulfillingPredicate.map(file => file.name).join(', ');
        const { messageId, message } = createMessageFunc(fileNames);

        infoBarAddHandler(messageId, [message]);
    }

    return filesFulfillingPredicate;
}

function filterEmptyFiles(
    files: AttachmentFile[],
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void
): AttachmentFile[] {
    const createMessageFunc = function createEmptyFilesMessage(fileNames: string): InfoBarMessage {
        const message = format(loc(WarningEmptyFilesCanNotBeAttached), fileNames);

        return {
            messageId: 'WarningEmptyFilesCanNotBeAttached',
            message: message,
        };
    };

    return filterAndShowErrorMessage(
        files,
        infoBarAddHandler,
        file => !(file.fileType === AttachmentFileType.Local && file.size === 0),
        createMessageFunc
    );
}

function filterUnsupportedInlineImages(
    files: AttachmentFile[],
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void
): AttachmentFile[] {
    const createMessageFunc = function createUnsupportedInlineImagesMessage(
        fileNames: string
    ): InfoBarMessage {
        const message = format(loc(WarningInlineImageTypeNotSupported), fileNames);

        return {
            messageId: 'WarningInlineImageTypeNotSupported',
            message: message,
        };
    };

    return filterAndShowErrorMessage(
        files,
        infoBarAddHandler,
        file => isImageFile(file.name, (<LocalComputerFile>file).type),
        createMessageFunc
    );
}

// The function runs various filter on the files and returns the filtered list of files along
// with adding any warning message into the info bar
export default function filterFilesToAttach(
    files: AttachmentFileList,
    isInline: boolean,
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    infoBarRemoveHandler: (infoBarMessageIds: string[]) => void
): AttachmentFileList {
    clearAttachmentInfoBarMessages(infoBarRemoveHandler);

    let filesArr = [];
    for (let i = 0; i < files.length; i++) {
        filesArr.push(files[i]);
    }

    // Add any other filters on files here by putting them into function and calling it here
    filesArr = filterEmptyFiles(filesArr, infoBarAddHandler);

    if (isInline) {
        filesArr = filterUnsupportedInlineImages(filesArr, infoBarAddHandler);
    }

    return filesArr;
}
